import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, UserPlus, Loader2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { FamilyMembersList } from "./FamilyMembersList";
import { AddFamilyMemberDialog } from "./AddFamilyMemberDialog";
import { useToast } from "@/components/ui/use-toast";
import { FamilyGroup } from "./types";

export function FamilyManagement() {
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [isFamilyEnabled, setIsFamilyEnabled] = useState(false);
  const { toast } = useToast();

  const { data: familyGroup, isLoading } = useQuery({
    queryKey: ['familyGroup'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*, family_group_id')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;

      if (!profile.family_group_id) {
        const { data: newGroup, error: groupError } = await supabase
          .from('family_groups')
          .insert([{ primary_member_id: user.id }])
          .select()
          .single();

        if (groupError) throw groupError;

        await supabase
          .from('profiles')
          .update({ family_group_id: newGroup.id, family_role: 'primary' })
          .eq('id', user.id);

        return { ...newGroup, members: [] } as FamilyGroup;
      }

      const { data: group, error: groupError } = await supabase
        .from('family_groups')
        .select(`
          *,
          members:profiles!profiles_family_group_id_fkey(
            id,
            full_name,
            email,
            family_role,
            relationship_verified
          )
        `)
        .eq('id', profile.family_group_id)
        .single();

      if (groupError) throw groupError;
      
      return {
        ...group,
        members: Array.isArray(group.members) ? group.members : []
      } as FamilyGroup;
    },
    enabled: isFamilyEnabled,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card className="w-full">
      <div className="p-6 space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Users className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">Family Management</h2>
              <p className="text-sm text-muted-foreground">
                Manage your family members and their access
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center space-x-2">
              <Switch
                checked={isFamilyEnabled}
                onCheckedChange={setIsFamilyEnabled}
                aria-label="Toggle family management"
              />
              <span className="text-sm text-muted-foreground">
                Enable Family Management
              </span>
            </div>
            {isFamilyEnabled && (
              <Button 
                onClick={() => setIsAddingMember(true)}
                className="whitespace-nowrap"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Add Family Member
              </Button>
            )}
          </div>
        </div>

        {isFamilyEnabled && (
          <>
            <FamilyMembersList members={familyGroup?.members || []} />
            {familyGroup && (
              <AddFamilyMemberDialog 
                open={isAddingMember} 
                onOpenChange={setIsAddingMember}
                familyGroupId={familyGroup.id}
              />
            )}
          </>
        )}
      </div>
    </Card>
  );
}